import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getVacationDataAction, getEmployeeVacationsAction, sendVacationsMessageAction } from "../actions";
import { getUserData, getIsExportingGrid } from "@icarius-app/selectors";
import {
    getData,
    getEmployeeMiscData,
    getIsLoading,
    getDateFormat,
    getLocale,
    getEmployeeDebitData,
    getEmployeeCreditData,
    getVacationsSchema,
    getContractDate,
    getVacationsUpdateDate,
    getOutcomeDate,
} from "../selectors";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import useIsSocietyCountry from "@icarius-app/components/wrapper/hooks/useIsSocietyCountry";

const useVacations = () => {
    const isPeru = useIsSocietyCountry("PE");

    const [graphsDialogIsOpen, setGraphsDialogIsOpen] = useState(false);
    const [employeeVacationsDialogIsOpen, setEmployeeVacationsDialogIsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [graphDialogData, setGraphDialogData] = useState(null);

    const dispatch = useDispatch();

    const exportingGridStatus = useSelector(getIsExportingGrid);
    const data = useSelector(getData);
    const isLoading = useSelector(getIsLoading);
    const dateFormat = useSelector(getDateFormat);
    const locale = useSelector(getLocale);
    const userData = useSelector(getUserData);
    const debitData = useSelector(getEmployeeDebitData);
    const creditData = useSelector(getEmployeeCreditData);
    const miscData = useSelector(getEmployeeMiscData);
    const vacationsSchema = useSelector(getVacationsSchema);
    const contractDate = useSelector(getContractDate);
    const outcomeDate = useSelector(getOutcomeDate);
    const vacationsUpdateDate = useSelector(getVacationsUpdateDate);

    useEffect(() => {
        dispatch(getVacationDataAction());
    }, [dispatch]);

    const createGraphData = (gridRef) => {
        let canGetEveryRowData = true;
        let filteredRows = [];

        gridRef.api.forEachNodeAfterFilter((row) => {
            if (!(row && row.data)) {
                canGetEveryRowData = false;
            } else {
                filteredRows.push(row);
            }
        });

        if (!canGetEveryRowData || !filteredRows.length) return null;

        if (isPeru) {
            const legalOthersVacations = {
                title: getLocalizedString("legalVacationsAndOthers"),
                data: {
                    [getLocalizedString("totalUsed")]: filteredRows.reduce(
                        (a, b) => a + (parseFloat(b.data["debitUsedTotal"]) || 0),
                        0
                    ),
                    [getLocalizedString("availableBalance")]: filteredRows.reduce(
                        (a, b) =>
                            a +
                            (parseFloat(b.data["pending"]) +
                                parseFloat(b.data["expired"]) +
                                parseFloat(b.data["truncated"]) || 0),
                        0
                    ),
                    [getLocalizedString("totalReservedForFutureUse")]: filteredRows.reduce(
                        (a, b) => a + (parseFloat(b.data["creditUnusedTotal"]) || 0),
                        0
                    ),
                    [getLocalizedString("totalLegalVacationsAndOthers")]: filteredRows.reduce(
                        (a, b) => a + (parseFloat(b.data["creditTotal"]) || 0),
                        0
                    ),
                },
            };

            return [legalOthersVacations];
        }

        const legalOthersVacations = {
            title: getLocalizedString("legalVacationsAndOthers"),
            data: {
                [getLocalizedString("totalUsed")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["debitUsedLegal"]) || 0),
                    0
                ),
                [getLocalizedString("pendingBalance")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["debitAvailableLegal"]) || 0),
                    0
                ),
                [getLocalizedString("totalReservedForFutureUse")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["creditUnusedLegal"]) || 0),
                    0
                ),
                [getLocalizedString("totalLegalVacationsAndOthers")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["creditLegal"]) || 0),
                    0
                ),
            },
        };

        const additionalVacations = {
            title: getLocalizedString("additionalVacation"),
            data: {
                [getLocalizedString("totalUsed")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["debitUsedAditional"]) || 0),
                    0
                ),
                [getLocalizedString("pendingBalance")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["debitAvailableAditional"]) || 0),
                    0
                ),
                [getLocalizedString("totalReservedForFutureUse")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["creditUnusedAditional"]) || 0),
                    0
                ),
                [getLocalizedString("totalLegalVacationsAndOthers")]: filteredRows.reduce(
                    (a, b) => a + (parseFloat(b.data["creditAditional"]) || 0),
                    0
                ),
            },
        };

        return [legalOthersVacations, additionalVacations];
    };

    const handleOpenMailDialog = (gridRef) => {
        let documents = gridRef.api.getSelectedRows();
        if (documents.length < 1) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRecord"), severity: "warning" })
            );
            return;
        }

        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("doYouWantToSendReminderMessageToEmployeesToRequestVacation"),
                onConfirm: () => dispatch(sendVacationsMessageAction({ codes: documents.map((item) => item.code) })),
            })
        );
    };

    const handleOpenGraphs = (gridRef) => {
        setGraphDialogData(createGraphData(gridRef));
        setGraphsDialogIsOpen(true);
    };

    const handleCloseGraphs = () => {
        setGraphsDialogIsOpen(false);
        setGraphDialogData(null);
    };

    const handleRowClick = ({ data }) => {
        dispatch(getEmployeeVacationsAction(userData.level, data.code)).then((resp) => {
            if (resp.status === 200) {
                setSelectedRow(data);
                setEmployeeVacationsDialogIsOpen(true);
            }
        });
    };

    const handleCloseSecondGrid = () => {
        setEmployeeVacationsDialogIsOpen(false);
        setSelectedRow(null);
    };

    const state = {
        graphsDialogIsOpen,
        employeeVacationsDialogIsOpen,
        selectedRow,
        graphDialogData,
    };

    const handlers = {
        handleOpenMailDialog,
        handleOpenGraphs,
        handleCloseGraphs,
        handleRowClick,
        handleCloseSecondGrid,
    };

    return {
        isLoading: isLoading || exportingGridStatus,
        data,
        dateFormat,
        locale,
        debitData,
        creditData,
        miscData,
        vacationsSchema,
        state,
        handlers,
        contractDate,
        outcomeDate,
        vacationsUpdateDate,
    };
};

export default useVacations;
